import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import GlobalHeader from "../../../../components/GlobalHeader.jsx";
import { fetchUserProfile, fetchEventsByUserId } from "../../../../utils/api/adminApi";
import "../css/ViewingOrganiser.css";
import OrganizerDetailsCard from "../components/OrganizerDetailsCard.jsx";
import InstitutionDetailsCard from "../components/InstitutionDetailsCard.jsx";
import EventsHostedCard from "../components/EventsHostedCard.jsx";
import { FaInfoCircle } from "react-icons/fa";

const ViewingOrganiser = () => {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const organizerId = location.state?.userId || id;

  const [formData, setFormData] = useState({
    user_id: "",
    name: "",
    title: "",
    email: "",
    cellNumber: "",
    institutionLocation: "",
    faculty: "",
    department: "",
    ieeeNumber: "",
    organVAT: "",
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrganizerData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!organizerId) {
          throw new Error("No organizer ID provided");
        }

        const response = await fetchUserProfile(organizerId);
        const orgData = response.data || response;

        if (!orgData) {
          throw new Error("No organizer data received");
        }

        setFormData({
          user_id: orgData.user_id || orgData.id || organizerId,
          name: orgData.name || "",
          title: orgData.title || "",
          email: orgData.email || "",
          cellNumber: orgData.cellNumber || "",
          company: orgData.company || "",
          vat_no: orgData.vat_no || "",
          bank_name: orgData.bank_name || "",
          bank_account_number: orgData.bank_account_number || "",
          bank_account_name: orgData.bank_account_name || "",
          paystack_subaccount_code: orgData.paystack_subaccount_code || "",
          payout_enabled: orgData.payout_enabled || false,
          role: orgData.role || "",
          is_disabled: orgData.is_disabled || false,
          created_at: orgData.created_at || null,
          updated_at: orgData.updated_at || null,
        });
      } catch (err) {
        console.error("Error fetching organizer data:", err);
        setError("Failed to load organizer data. Please try again.");
        setFormData({
          user_id: organizerId || "",
          name: "",
          title: "",
          email: "",
          cellNumber: "",
          company: "",
          vat_no: "",
          bank_name: "",
          bank_account_number: "",
          bank_account_name: "",
          paystack_subaccount_code: "",
          payout_enabled: false,
          role: "",
          is_disabled: false,
          created_at: null,
          updated_at: null,
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchEventsData = async () => {
      try {
        if (!organizerId) return;
        const response = await fetchEventsByUserId(organizerId);
        const eventData = response.data || response;
        setEvents(eventData || []);
      } catch (err) {
        console.error("Error fetching events data:", err);
        setEvents([]);
      }
    };

    if (!organizerId) {
      setError("No organizer ID provided");
      setLoading(false);
      return;
    }

    fetchOrganizerData();
    fetchEventsData();
  }, [organizerId]);

  if (loading) {
    return (
      <div className="viewing-organiser-page">
        <GlobalHeader
          title="Organiser Profile"
          subtitle="View and manage organiser information and events"
          backTo="/users"
          logoutTo="/"
        />
        <main className="viewing-organiser-main">
          <div className="viewing-organiser-loading">
            <div className="viewing-organiser-loading__spinner" />
            <p className="viewing-organiser-loading__text">
              Loading organiser profile...
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="viewing-organiser-page">
      <GlobalHeader
        title="Organiser Profile"
        subtitle="View and manage organiser information and events"
        backTo="/users"
        logoutTo="/"
      />
      <main className="viewing-organiser-main">
        {error && (
          <div className="viewing-organiser-error">
            <FaInfoCircle />
            <p className="viewing-organiser-error__text">{error}</p>
          </div>
        )}

        <div className="viewing-organiser-layout">
          <div className="viewing-organiser-cards-grid">
            <OrganizerDetailsCard formData={formData} />
            <InstitutionDetailsCard formData={formData} />
          </div>
        </div>

        <div className="viewing-organiser-events-section">
          <EventsHostedCard events={events} />
        </div>
      </main>
    </div>
  );
};

export default ViewingOrganiser;
